<template>
  <side-panel-inner
    :headerTitle="`Submit a ${$store.state.documents.docType.singular}`"
    class="p-document-create"
    v-if="ready"
  >
    <template slot="actions">
      <base-button primary="true" @click="onSubmit" iconBefore="pencil"
        >Submit</base-button
      >
    </template>

    <!-- document Title -->
    <form
      v-if="!creating"
      @submit.prevent="onSubmit"
      novalidate="true"
      autocomplete="off"
    >
      <message type="error" v-if="showValidationMessage">
        <h3>Form Invalid</h3>
        <p>The form is invalid, please check and try again.</p>
        <ul>
          <li v-for="(error, index) in errors.items" :key="index">{{ error.msg }}</li>
        </ul>
      </message>

      <form-field
        full-width
        :label="(docType.singular + ' Title') | capitalize"
        name="document_title"
        :helper-text="
          'NOTE: If the ' +
            docType.singular +
            ' is a new verison it must be named as the others for consistancy'
        "
        v-model="formData.title"
        v-validate="{ required: true, min: 5 }"
        data-vv-as="title"
        :error="errors.first('document_title')"
        :max-length="150"
        :show-counter="true"
        required
      ></form-field>

      <!-- Choose Primary Category -->
      <form-field
        label="Choose Category"
        required
        name="document_category"
        helper-text="Select primary category"
      >
        <form-multiselect
          :show-valid-tick="true"
          v-model="formData.primary_category"
          placeholder
          :options="filteredPrimaryCategories"
          :multiple="false"
          v-validate="{ required: true }"
          data-vv-as="primary category"
          id="document_category"
          name="document_category"
          :error="errors.first('document_category')"
          label="name"
          track-by="name"
        ></form-multiselect>
      </form-field>

      <!-- Additional Categories -->
      <form-field
        name="document_additional_categories"
        helper-text="Select additional categories"
      >
        <form-multiselect
          :show-valid-tick="true"
          v-model="formData.categories"
          v-validate="{ is_not: formData.primary_category }"
          data-vv-as="additional categories"
          :error="errors.first('document_additional_categories')"
          name="document_additional_categories"
          :options="filteredAdditionalCategories"
          placeholder
          :multiple="true"
          label="name"
          track-by="name"
        ></form-multiselect>
      </form-field>

      <!-- Document types -->
      <form-field
        label="Document type"
        name="document_type"
        helper-text="Define the type of document this is"
        required
      >
        <form-multiselect
          :show-valid-tick="true"
          v-model="formData.type"
          v-validate="{ required: true }"
          data-vv-as="document type"
          :error="errors.first('type')"
          name="type"
          :options="documentTypes"
          placeholder
          :multiple="false"
          label="name"
          track-by="name"
        ></form-multiselect>
      </form-field>

      <!-- Add Tags -->
      <form-field
        label="Add Tags"
        name="document_tags"
        :helper-text="
          'Help users find your ' +
            docType.singular +
            ' by tagging relevant keywords'
        "
      >
        <form-multiselect
          :show-valid-tick="true"
          v-model="formData.tags"
          :options="tags"
          :multiple="true"
          label="name"
          track-by="name"
          :taggable="true"
          @tag="add"
        ></form-multiselect>
      </form-field>

      <!-- Initial Version Number -->
      <form-field
        label="Initial Version Number"
        name="document_starting_version"
        :helper-text="
          'Set the starting version number for the ' + docType.singular
        "
        v-model="formData.startingVersion"
        v-validate="{ required: true, min_value: 1, decimal: 1 }"
        data-vv-as="initial version number"
        :error="errors.first('document_starting_version')"
        required
      ></form-field>

      <!-- EqIA Number -->
      <form-field
        full-width
        :label="(docType.singular + ' EqIA Number') | capitalize"
        name="document_eqia_number"
        v-model="formData.eqiaNumber"
        v-validate="{ required: true, min: 5 }"
        data-vv-as="eqia number"
        :error="errors.first('document_eqia_number')"
        :max-length="50"
        :show-counter="true"
        required
        v-if="docType.singular === 'policy'"
      ></form-field>

      <!-- Ratified Group -->
      <form-field
        label="Ratified Group"
        required
        name="document_ratified_group"
        helper-text="Select ratified group"
        v-if="docType.singular === 'policy'"
      >
        <form-multiselect
          :show-valid-tick="true"
          v-model="formData.ratifiedGroup"
          placeholder
          :options="ratifiedGroups"
          :multiple="false"
          v-validate="{ required: true }"
          data-vv-as="ratified group"
          id="document_ratified_group"
          name="document_ratified_group"
          :error="errors.first('document_ratified_group')"
          label="label"
          track-by="label"
        ></form-multiselect>
      </form-field>

      <!-- File Upload -->
      <section class="form-src" v-if="docType.singular === 'form'">
        <tabs>
          <tabs-item
            :key="0"
            :active="showUpload === true"
            @click="showUpload = true"
            >File Upload</tabs-item
          >
          <tabs-item
            :key="1"
            :active="showUpload === false"
            @click="showUpload = false"
            >Web Form</tabs-item
          >
        </tabs>

        <form-field
          label="File upload"
          name="document_file"
          :helper-text="'Upload your ' + docType.singular"
          v-if="showUpload === true"
        >
          <file-upload
            :fileTypes="documentFileTypes"
            :docType="docType"
            :resource="docType.singular"
            name="document_file"
            @uploaded="onFileUploaded"
            data-vv-as="file upload"
            :error="errors.first('document_file')"
            ref="form_file"
          ></file-upload>
        </form-field>

        <form-field
          full-width
          label="Web Form"
          name="external_resource"
          v-model="formData.external_resource"
          v-validate="{ required: true, min: 5 }"
          data-vv-as="title"
          :error="errors.first('external_resource')"
          v-else
        ></form-field>
      </section>

      <form-field
        label="File upload"
        name="document_file"
        :helper-text="'Upload your ' + docType.singular"
        required
        v-else
      >
        <file-upload
          :fileTypes="documentFileTypes"
          :docType="docType"
          :resource="docType.singular"
          name="document_file"
          @uploaded="onFileUploaded"
          v-validate="{ required: true }"
          data-vv-as="file upload"
          :error="errors.first('document_file')"
        ></file-upload>
      </form-field>

      <!-- Choose Review Date -->
      <form-field
        label="Choose Review Date"
        name="document_review_date"
        :helper-text="'Select your ' + docType.singular + ' review date'"
        required
      >
        <form-datepicker
          small
          v-model="formData.current_version.review_date"
          name="document_review_date"
          v-validate="{ required: true }"
          data-vv-as="review date"
          id="document_review_date"
          format="DD / MM / YYYY"
          :error="errors.first('document_review_date')"
        ></form-datepicker>
      </form-field>

      <!-- :disabled-dates="{to: $dayjs().subtract(1, 'day')}" -->
      <!-- Responsible Users -->
      <form-field
        label="Responsible Users"
        name="responsible_users"
        required
        :helper-text="'Give people responsibility of this ' + docType.singular"
      >
        <div class="row">
          <div class="col-xs-12">
            <form-label small required>Primary</form-label>
            <user-select
              v-model="formData.responsibleUser.primary"
              id="document_primary_user"
              name="document_primary_user"
              v-validate="{ required: true }"
              data-vv-as="primary user"
              :error="errors.first('document_primary_user')"
              :permissions="permissions"
            ></user-select>
          </div>
          <div class="col-xs-12">
            <form-label small>Secondary</form-label>
            <user-select
              v-model="formData.responsibleUser.secondary"
              id="document_secondary_user"
              name="document_secondary_user"
              v-validate="{
                is_not: formData.responsibleUser.primary,
              }"
              data-vv-as="secondary user"
              :error="errors.first('document_secondary_user')"
              :permissions="permissions"
            ></user-select>
          </div>
        </div>
      </form-field>
    </form>
    <loading v-if="creating"></loading>
  </side-panel-inner>
</template>

<script>
import FileUpload from "@/components/FileUpload";
import UserSelect from "@/components/UserSelect";
import ratifiedGroupOptions from "@/data/ratifiedGroupOptions";
import { categoryFilters } from "@/mixins/categoryFilters";

export default {
  name: "document-create",

  data() {
    return {
      permissions: ["add_policies"],
      ready: false,
      showUpload: true,
      showValidationMessage: false,
      creating: false,
      documentTypes: [
        { id: "policies", name: "Policy" },
        { id: "guidelines", name: "Guideline" },
        { id: "procedures", name: "Standard Operating Procedure" },
      ],
      formData: {
        title: "",
        tags: [],
        primary_category: "",
        categories: [],
        eqiaNumber: "",
        ratifiedGroup: "",
        startingVersion: 1,
        current_version: {
          review_date: this.$dayjs()
            .add(3, "year")
            .format("YYYY-MM-DD"),
        },
        external_resource: "",
        responsibleUser: {
          primary: this.$store.state.auth.userId || "",
          secondary: "",
        },
      },
    };
  },

  beforeCreate() {
    this.$store.dispatch("categories/list");
    this.$store.dispatch("tags/listDocumentTags").then(() => (this.ready = true));
  },

  methods: {
    onSubmit() {
      const vm = this;
      this.$validator.validate().then((result) => {
        if (!result) {
          vm.showValidationMessage = true;
        } else {
          vm.showValidationMessage = false;
          vm.createDocument();
        }
      });
    },

    add(newTag) {
      const formTag = {
        id: null,
        name: newTag,
      };

      this.formData.tags.push(formTag);
      this.$store.dispatch("tags/createDocumentTag", {name: newTag});
    },

    onFileUploaded(fileData) {
      this.$store.commit("documents/setFileId", fileData.id);
    },

    createDocument() {
      const vm = this;
      const formData = this.formData;

      if (!this.formIsValid) {
        this.showValidationMessage = true;
        return;
      }
      this.creating = true;

      const tagIds = [];

      formData.tags.forEach((tag) => {
        if (tag.id) {
          tagIds.push(tag.id);
        } else {
          tagIds.push(tag.name);
        }
      });

      const documentData = {
        title: formData.title,
        description: "Description Text",
        primary_category_id: formData.primary_category.id,
        primary_user_id: formData.responsibleUser.primary.id,
        secondary_user_id: formData.responsibleUser.secondary.id,
        tags: formData.tags,
        category_ids: formData.categories.map((cat) => cat.id), //we only need ids
        starting_version: formData.startingVersion,
        tags: formData.tags.map((tag) => tag.id), //we only need ids
        // eqia_number: formData.eqiaNumber,
        // ratified_group_id: formData.ratifiedGroup.id,
        review_date: formData.current_version.review_date,
        external_resource: formData.external_resource,
        type: formData.type.id
      };

      if (formData.eqiaNumber) {
        documentData.eqia_number = formData.eqiaNumber;
      }
      if (formData.ratifiedGroup.id) {
        documentData.ratified_group_id = formData.ratifiedGroup.id;
      }

      this.$store
        .dispatch("documents/createAndPublish", documentData)
        .then(() => {
          // If we have a review date, we need to update the version
          // const dateArgs = {
          //   policyId: formData.id,
          //   versionId: formData.current_version.id,
          //   payload: {
          //     review_date: formData.current_version.review_date
          //   }
          // };
          // this.$store.dispatch('documentVersions/update',dateArgs);
          vm.$store.dispatch("CLOSE_PANEL");
        })
        .catch((err) => {
          this.showValidationMessage = true;
        });
    },
  },

  computed: {
    documentFileTypes() {
      if (this.docType.singular === "policy") {
        return ["pdf","xls","xlsx"];
      }

      return null;
    },

    docType() {
      return this.$store.state.documents.docType;
    },

    tags() {
      return this.$store.state.tags.collection;
    },

    categories() {
      return this.$store.state.categories.categories;
    },

    ratifiedGroups() {
      return ratifiedGroupOptions;
    },

    additionalCatIds() {
      const additionalCatIds = [];
      this.formData.categories.forEach((category) => {
        additionalCatIds.push(category.id);
      });
      return additionalCatIds;
    },

    formIsValid() {
      if (this.docType.singular === "policy") {
        return (
          this.formData.title &&
          this.formData.primary_category &&
          this.formData.eqiaNumber &&
          this.$store.state.documents.responseData.fileId
        );
      } else {
        return (
          this.formData.title &&
          this.formData.primary_category &&
          (this.$store.state.documents.responseData.fileId ||
            this.formData.external_resource)
        );
      }
    },

    tagsAsIds() {
      const tagsAsIds = [];
      this.formData.tags.forEach((tag) => {
        tagsAsIds.push(tag.id);
      });
      return tagsAsIds;
    },
  },

  components: {
    FileUpload,
    UserSelect,
  },

  mixins: [categoryFilters],
};
</script>

<style lang="scss"></style>
